/**
 * Constructor.io — Base web component
 *
 * Shared logic for all Constructor surface components:
 *   - Timeout / race pattern
 *   - Handle extraction from Constructor API response
 *   - Fetching and rendering product HTML from Shopify
 *
 * Subclasses should implement:
 *   connectedCallback()          — read attributes, decide when to trigger _load()
 *   _fetchConstructorResults()   — which Constructor API surface to call
 *   _renderHtml(htmlText)        — how to insert fetched products into the DOM
 *
 * Subclasses can implement:
 *   _onError()                   — called on timeout or API failure (default: no-op)
 *   _onNoResults()               — called when Constructor returns 0 results (default: no-op)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ConstructorPaginatedBase extends ConstructorBase
 *
 * Additional shared logic for components that paginate results (browse, search):
 *   - Load More button + progress bar
 *   - Hidden numbered <nav> for SEO crawlers
 *   - ?page= URL param read on init and written on navigation
 *   - Next-page prefetch (Constructor + parsed product fragment) so Load more
 *     can skip the network wait and DOMParser when the user clicks
 *
 * Subclasses should call _initPagination() inside connectedCallback() and
 * store total_num_results into this._totalNumResults inside _fetchConstructorResults().
 * Paginated surfaces that support prefetch should implement
 * _requestConstructorForPrefetch(nextPage) (no side effects until apply()).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ConstructorFilteredPaginatedBase extends ConstructorPaginatedBase
 *
 * Shared filter + sort + URL-sync logic for surfaces that render Constructor
 * results with facets and sortable options (browse, search). Keeps
 * ConstructorPaginatedBase free of filter concerns so paginated-but-unfiltered
 * surfaces (e.g. reco) are not polluted.
 *
 * Subclasses must implement:
 *   _surfaceName()         — 'browse' | 'search'; drives event names.
 *   _sortRadioIdPrefix()   — DOM id prefix for sort radios.
 *
 * Subclasses should call _initFilterAndSort() inside connectedCallback().
 * _resetForFilterChange() may be overridden; call super first to retain the
 * shared reset (page, initial-render flag, fetched count, ?page= cleanup).
 */
if (window.ConstructorioClient && window._constructorApiKey && !window.constructorio) {
  window.constructorio = new window.ConstructorioClient({ apiKey: window._constructorApiKey });
}

class ConstructorBase extends HTMLElement {
  static TIMEOUT_DURATION = 3000;
  // Below this width, body lock uses iOS-safe `overflow-hidden-mobile` instead of `overflow-hidden`.
  static MOBILE_BODY_LOCK_PX = 750;
  // Drawer close delay — must mirror the CSS transition duration on .menu-opening so
  // `[open]` stays for the entire animation. Update both together if either changes.
  static DRAWER_CLOSE_MS = 400;

  // Strings in window.constructorStrings come from Liquid `| t` and are HTML-entity-encoded
  // (e.g. `"` → `&quot;`, `'` → `&#39;`). When written via innerHTML the parser decodes
  // them automatically, but textContent renders entities literally. Use this on any
  // translated string before assigning it to .textContent.
  static _decodeEntities(str) {
    if (typeof str !== 'string' || str.indexOf('&') === -1) return str;
    const ta = document.createElement('textarea');
    ta.innerHTML = str;
    return ta.value;
  }

  // Wait until after the next paint. rAF alone runs *before* paint;
  // scheduler.yield() / setTimeout(0) can also resume before a frame.
  static _yieldToPaint() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        setTimeout(resolve, 0);
      });
    });
  }

  disconnectedCallback() {
    if (this._timeout) clearTimeout(this._timeout);
  }

  async _load() {
    try {
      // QA: ?constructor_force_error=1 — UAT/preview always; production only if the theme setting is on.
      const forceRequested = new URLSearchParams(window.location.search).has('constructor_force_error');
      const forceAllowed =
        window.themeSettings?.constructor_force_error_enabled ||
        /uat\.dedoles\.|myshopify\.com|localhost/.test(window.location.hostname);
      if (forceRequested && forceAllowed) throw new Error('timeout');

      const timeoutPromise = new Promise(
        (_, reject) =>
          (this._timeout = setTimeout(() => reject(new Error('timeout')), ConstructorBase.TIMEOUT_DURATION)),
      );

      const result = await Promise.race([this._fetchConstructorResults(), timeoutPromise]);

      clearTimeout(this._timeout);

      // Load-more (page > 1) must not rebuild facets/sort — filters did not change.
      if (this._shouldUpdateFilterChrome?.() !== false) {
        await this._afterFetchResult?.(result);
      }

      const handles = this._extractHandles(result);
      if (handles.length > 0) {
        this._fetchAndRenderProducts(handles);
      } else {
        console.log(`[${this.constructor.name}] No results from Constructor.`);
        this._onNoResults?.();
      }
    } catch (error) {
      console.error(`[${this.constructor.name}] Failed to load:`, error.message);
      this._reportError(error, error?.message === 'timeout' ? 'timeout' : 'api');
      this._onError?.();
    }
  }

  _reportError(error, errorType, extraContext = {}) {
    if (error?.name === 'AbortError') return;
    window.reportFrontendError?.({
      source: 'constructor',
      surface: this.constructor.name,
      message: error?.message || String(error || 'Unknown Constructor error'),
      stack: error?.stack || '',
      error_type: errorType,
      context: {
        page: this.currentPage,
        ...(this.filterName ? { filterName: this.filterName, filterValue: this.filterValue } : {}),
        ...(this.searchTerm ? { searchTerm: this.searchTerm } : {}),
        ...(this.productType ? { productType: this.productType } : {}),
        ...extraContext,
      },
    });
  }

  async _loadNativeSectionFallback() {
    if (!this.fallbackUrl) return false;
    const rootSelector = this.tagName === 'CONSTRUCTOR-SEARCH' ? '.template-search' : '.section__product-grid';

    try {
      const url = new URL(this.fallbackUrl, window.location.origin);
      url.searchParams.set('view', 'native');
      new URLSearchParams(window.location.search).forEach((value, key) => {
        if (key === 'constructor_force_error' || key === 'view') return;
        if (!url.searchParams.has(key)) url.searchParams.set(key, value);
      });
      if (this.searchTerm) url.searchParams.set('q', this.searchTerm);

      const response = await fetch(url.href);
      if (!response.ok) throw new Error(`Native fallback responded with ${response.status}`);

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = parsed.querySelector(rootSelector);
      const currentRoot = this.closest(rootSelector);
      if (!incoming || !currentRoot) throw new Error('Native fallback markup missing');

      document.documentElement.dataset.shopifyNativeFallback = '1';
      currentRoot.replaceWith(incoming);
      return true;
    } catch (error) {
      console.error(`[${this.constructor.name}] Native fallback failed:`, error);
      this._reportError(error, 'fallback_failed');
      return false;
    }
  }

  _extractHandles(result) {
    const results = result?.response?.results;
    if (!Array.isArray(results)) return [];
    return results
      .map((item) => item.data?.handle || item.data?.id)
      .filter(Boolean)
      .map((h) => encodeURIComponent(h));
  }

  _resolveProductsLimit(_handles) {
    return this.productsToShow;
  }

  _normalizeHandles(handles) {
    const uniqueHandlesSet = new Set(handles);
    (this.excludedHandles || []).forEach((h) => uniqueHandlesSet.delete(h));
    const limit = this._resolveProductsLimit([...uniqueHandlesSet]);
    return [...uniqueHandlesSet].slice(0, limit);
  }

  async _fetchProductHtml(handles, { priority, signal } = {}) {
    const uniqueHandles = this._normalizeHandles(handles);
    if (!uniqueHandles.length) return '';

    // Shopify's all_products global is limited to 20 unique product lookups
    // per request — exceeding it causes the 20th product to be returned for
    // every subsequent handle, producing duplicates. Batch to stay within the limit.
    const baseUrl = window.routes?.all_products_collection_url || '/collections/all';
    const BATCH_SIZE = 20;
    const batches = [];
    for (let i = 0; i < uniqueHandles.length; i += BATCH_SIZE) {
      batches.push(uniqueHandles.slice(i, i + BATCH_SIZE));
    }

    const campaign_code = new URLSearchParams(window.location.search).get('code');
    const fetchOpts = {};
    if (priority) fetchOpts.priority = priority;
    if (signal) fetchOpts.signal = signal;

    const htmlParts = await Promise.all(
      batches.map((batch) => {
        const url =
          `${baseUrl}?view=list-products&limit=${batch.length}&type=${batch.join('+')}` +
          (campaign_code ? `&q=${campaign_code}` : '');
        return fetch(url, fetchOpts).then((r) => {
          if (!r.ok) throw new Error(`Network response was not ok: ${r.statusText}`);
          return r.text();
        });
      }),
    );

    return htmlParts.join('');
  }

  async _fetchAndRenderProducts(handles) {
    if (!handles || handles.length === 0) return;
    if (!this.productsContainer) return;

    try {
      const html = await this._fetchProductHtml(handles);
      if (!html) return;
      this._renderHtml(html);
      this._schedulePrefetchNextPage?.();
    } catch (error) {
      console.error(`[${this.constructor.name}] Fetch or render error:`, error);
      this._reportError(error, 'shopify_html');
      this._resetLoadMoreButton?.();
    }
  }
}

class ConstructorPaginatedBase extends ConstructorBase {
  _initPagination() {
    this.paginationContainer = this.querySelector('.js-pagination-container');
    const urlPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
    this.currentPage = urlPage > 0 ? urlPage : 1;
    this._isInitialRender = true;
    this._totalNumResults = 0;
    this.statusLabel = window.constructorStrings?.paginationStatus || '__size__ / __total__';
    this._prefetch = null;
    this._prefetchGeneration = 0;
    this._prefetchIdleId = null;
    this._prefetchAbort = null;
    this._prefetchResumeTimer = null;
    this._armPrefetchInteractionGuard();
  }

  async _load() {
    // Prefer a ready prefetch for this page — skips Constructor + Shopify waits.
    const cached = await this._claimPrefetch(this.currentPage);
    if (cached?.parsed) {
      try {
        cached.apply?.();
        if (this._shouldUpdateFilterChrome()) {
          await this._afterFetchResult?.(cached.result);
        }
        this._renderHtml(cached.parsed);
        this._schedulePrefetchNextPage();
      } catch (error) {
        // Do not fall through to network — apply() may already have mutated offsets.
        console.error(`[${this.constructor.name}] Prefetch consume failed:`, error);
        this._reportError(error, 'prefetch');
        this._resetLoadMoreButton?.();
      }
      return;
    }

    return super._load();
  }

  // Facets/sort only change on first paint or a filter/sort reset (page 1).
  _shouldUpdateFilterChrome() {
    return this._isInitialRender || this.currentPage === 1;
  }

  _armPrefetchInteractionGuard() {
    if (this._prefetchGuardBound) return;
    this._prefetchGuardBound = true;

    // Abort in-flight/idle prefetch when the user interacts, so background work
    // does not inflate input delay (INP). Keep a fully ready cache intact.
    const onInteract = () => {
      this._pausePrefetchForInteraction();
    };
    document.addEventListener('pointerdown', onInteract, { passive: true });
    document.addEventListener('keydown', onInteract, { passive: true });
    this._prefetchGuardOff = () => {
      document.removeEventListener('pointerdown', onInteract);
      document.removeEventListener('keydown', onInteract);
    };
  }

  _pausePrefetchForInteraction() {
    // Ready cache is safe to keep — consuming it on click is the fast path.
    if (this._prefetch?.status === 'ready') return;

    this._prefetchGeneration = (this._prefetchGeneration || 0) + 1;
    if (this._prefetchIdleId != null) {
      if (typeof cancelIdleCallback === 'function') cancelIdleCallback(this._prefetchIdleId);
      else clearTimeout(this._prefetchIdleId);
      this._prefetchIdleId = null;
    }
    try {
      this._prefetchAbort?.abort();
    } catch (_) {
      /* ignore */
    }
    this._prefetchAbort = null;
    this._prefetch = null;

    // Resume prefetch only after the user has been quiet — protects INP.
    clearTimeout(this._prefetchResumeTimer);
    this._prefetchResumeTimer = setTimeout(() => {
      this._schedulePrefetchNextPage();
    }, 1500);
  }

  _clearPrefetch() {
    this._prefetchGeneration = (this._prefetchGeneration || 0) + 1;
    if (this._prefetchIdleId != null) {
      if (typeof cancelIdleCallback === 'function') cancelIdleCallback(this._prefetchIdleId);
      else clearTimeout(this._prefetchIdleId);
      this._prefetchIdleId = null;
    }
    clearTimeout(this._prefetchResumeTimer);
    this._prefetchResumeTimer = null;
    try {
      this._prefetchAbort?.abort();
    } catch (_) {
      /* ignore */
    }
    this._prefetchAbort = null;
    this._prefetch = null;
  }

  async _claimPrefetch(page) {
    const entry = this._prefetch;
    if (!entry || entry.page !== page) return null;

    // Only consume a fully ready cache. Never await in-flight prefetch after click.
    if (entry.status === 'ready' && entry.parsed?.fragment) {
      this._prefetch = null;
      return entry;
    }
    return null;
  }

  _schedulePrefetchNextPage() {
    if (typeof this._requestConstructorForPrefetch !== 'function') return;

    const nextPage = this.currentPage + 1;
    const totalPages = Math.ceil((this._totalNumResults || 0) / (this.productsToShow || 1));
    if (!this._hasMore(totalPages)) return;
    if (this._prefetch?.page === nextPage) return;

    const generation = (this._prefetchGeneration = (this._prefetchGeneration || 0) + 1);
    const entry = { page: nextPage, status: 'idle', promise: null };
    this._prefetch = entry;

    const run = () => {
      if (entry.status !== 'idle') return entry.promise;
      entry.status = 'pending';
      const abort = new AbortController();
      this._prefetchAbort = abort;
      entry.promise = (async () => {
        try {
          const requested = await this._requestConstructorForPrefetch(nextPage);
          if (!requested?.result || generation !== this._prefetchGeneration || abort.signal.aborted) return;

          const handles = this._extractHandles(requested.result);
          if (!handles.length) return;

          const html = await this._fetchProductHtml(handles, { priority: 'low', signal: abort.signal });
          if (!html || generation !== this._prefetchGeneration || abort.signal.aborted) return;

          // Parse off the click path so Load more only appends a ready fragment.
          const parsed = this._parseProductItems(html);
          if (!parsed || generation !== this._prefetchGeneration || abort.signal.aborted) return;

          entry.result = requested.result;
          entry.apply = requested.apply;
          entry.parsed = parsed;
          entry.status = 'ready';
        } finally {
          // Non-success paths must not leave status 'pending' or same-page reschedule is blocked.
          if (entry.status !== 'ready' && this._prefetch === entry) this._prefetch = null;
        }
      })().catch(() => {
        if (this._prefetch === entry) this._prefetch = null;
      });
      return entry.promise;
    };

    // No timeout — a forced run on a busy mobile collection page lands in the
    // same window as scroll / Load more and inflates INP.
    if (typeof requestIdleCallback === 'function') {
      this._prefetchIdleId = requestIdleCallback(() => {
        this._prefetchIdleId = null;
        if (generation !== this._prefetchGeneration) return;
        run();
      });
    } else {
      this._prefetchIdleId = setTimeout(() => {
        this._prefetchIdleId = null;
        if (generation !== this._prefetchGeneration) return;
        run();
      }, 500);
    }
  }

  _getPaginationParams() {
    const isDeepLink = this._isInitialRender && this.currentPage > 1;
    return {
      page: isDeepLink ? 1 : this.currentPage,
      resultsPerPage: isDeepLink ? this.currentPage * this.productsToShow : this.productsToShow,
    };
  }

  _parseProductItems(htmlText) {
    if (!htmlText) return null;
    const parser = new DOMParser();
    const parsedHTML = parser.parseFromString(htmlText, 'text/html');
    const parsedElements = parsedHTML.querySelectorAll('.js-fetched-item');
    if (!parsedElements.length) return null;
    const fragment = document.createDocumentFragment();
    parsedElements.forEach((element) => {
      if (this.itemClasses?.length > 0) element.classList.add(...this.itemClasses);
      fragment.appendChild(element);
    });
    return { fragment, count: parsedElements.length };
  }

  _commitProductItems({ fragment, count }) {
    if (!this.productsContainer || !fragment) return;

    if (this.currentPage === 1 || this._isInitialRender) {
      this.productsContainer.innerHTML = '';
      this._renderedCount = 0;
      this._isInitialRender = false;
    }

    this._renderedCount = (this._renderedCount || 0) + count;
    window.updateWishlistStateImmediately?.(fragment);
    this.productsContainer.appendChild(fragment);
    this._renderPagination();
  }

  // Accepts Shopify HTML or a prefetch-parsed { fragment, count }.
  _renderHtml(htmlOrParsed) {
    const parsed = typeof htmlOrParsed === 'string' ? this._parseProductItems(htmlOrParsed) : htmlOrParsed;
    if (!parsed?.fragment) return;
    this._commitProductItems(parsed);
  }

  _hasMore(totalPages) {
    return this.currentPage < totalPages;
  }

  _renderPagination() {
    if (!this.paginationContainer) return;

    const totalPages = Math.ceil(this._totalNumResults / this.productsToShow);
    const shownItems = this._renderedCount;
    const progressPct = (shownItems / this._totalNumResults) * 100;
    const hasMore = this._hasMore(totalPages);
    const statusText = this.statusLabel.replace('__size__', shownItems).replace('__total__', this._totalNumResults);

    // Build the shell once, then only patch what changes — no full rebuild per click.
    if (!this._paginationBuilt) {
      const wrapperTpl = document.getElementById('constructor-pagination');
      if (!wrapperTpl) {
        console.warn(`[${this.constructor.name}] Missing #constructor-pagination template — skipping pagination render.`);
        return;
      }
      const fragment = wrapperTpl.content.cloneNode(true);
      this.paginationContainer.innerHTML = '';
      this.paginationContainer.appendChild(fragment);

      // Cache the nodes that get updated on every load.
      this._statusEl = this.paginationContainer.querySelector('[data-pagination-status]');
      this._progressEl = this.paginationContainer.querySelector('[data-pagination-progress]');
      this._loadMoreBtnEl = this.paginationContainer.querySelector('.js-constructor-load-more');
      this._navEl = this.paginationContainer.querySelector('[data-pagination-nav]');
      this._listEl = this.paginationContainer.querySelector('[data-pagination-list]');

      if (this._loadMoreBtnEl) {
        this._loadMoreBtnEl.addEventListener('click', () => {
          this._loadMoreBtnEl.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
          this._loadMoreBtnEl.disabled = true;
          ConstructorBase._yieldToPaint().then(() => {
            this.currentPage += 1;
            history.pushState({}, '', this._buildPageUrl(this.currentPage));
            this._load();
          });
        });
      }
      this._paginationBuilt = true;
    }

    // Patch only the parts that change between loads.
    if (this._statusEl) this._statusEl.innerHTML = statusText;
    if (this._progressEl) this._progressEl.style.width = `${progressPct}%`;
    if (this._loadMoreBtnEl) {
      this._loadMoreBtnEl.classList.toggle('hidden', !hasMore);
      this._loadMoreBtnEl.disabled = false;
      this._loadMoreBtnEl.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
    }
    this._loadMoreBtn = hasMore ? this._loadMoreBtnEl : null;

    if (totalPages > 1 && this._navEl && this._listEl) {
      const navItems = this._buildPaginationNav(totalPages);
      this._listEl.innerHTML = '';
      if (navItems) this._listEl.appendChild(navItems);
      this._navEl.hidden = false;
    } else if (this._navEl) {
      this._navEl.hidden = true;
    }
  }

  _buildPaginationNav(totalPages) {
    if (totalPages <= 1) return null;

    const current = this.currentPage;
    const pagesToShow = new Set([1, totalPages]);
    for (let i = Math.max(1, current - 2); i <= Math.min(totalPages, current + 2); i++) {
      pagesToShow.add(i);
    }
    const sorted = [...pagesToShow].sort((a, b) => a - b);

    const prevTpl = document.getElementById('constructor-pagination-prev');
    const nextTpl = document.getElementById('constructor-pagination-next');
    const pageTpl = document.getElementById('constructor-pagination-page');
    const pageCurrentTpl = document.getElementById('constructor-pagination-page-current');
    const dotsTpl = document.getElementById('constructor-pagination-dots');

    const out = document.createDocumentFragment();

    if (prevTpl) {
      const frag = prevTpl.content.cloneNode(true);
      const link = frag.querySelector('[data-pagination-prev]');
      if (link) {
        if (current > 1) {
          link.setAttribute('href', this._buildPageUrl(current - 1));
        } else {
          link.setAttribute('aria-disabled', 'true');
          link.classList.add('disabled');
        }
      }
      out.appendChild(frag);
    }

    sorted.forEach((page, idx) => {
      if (idx > 0 && page - sorted[idx - 1] > 1 && dotsTpl) {
        out.appendChild(dotsTpl.content.cloneNode(true));
      }
      if (page === current && pageCurrentTpl) {
        const frag = pageCurrentTpl.content.cloneNode(true);
        const link = frag.querySelector('[data-pagination-page-current]');
        if (link) link.textContent = page;
        out.appendChild(frag);
      } else if (pageTpl) {
        const frag = pageTpl.content.cloneNode(true);
        const link = frag.querySelector('[data-pagination-page]');
        if (link) {
          link.textContent = page;
          link.setAttribute('href', this._buildPageUrl(page));
          link.setAttribute('aria-label', String(page));
        }
        out.appendChild(frag);
      }
    });

    if (nextTpl) {
      const frag = nextTpl.content.cloneNode(true);
      const link = frag.querySelector('[data-pagination-next]');
      if (link) {
        if (current < totalPages) {
          link.setAttribute('href', this._buildPageUrl(current + 1));
        } else {
          link.classList.add('disabled');
          link.setAttribute('aria-disabled', 'true');
        }
      }
      out.appendChild(frag);
    }

    return out;
  }

  _resetLoadMoreButton() {
    if (!this._loadMoreBtn) return;
    this._loadMoreBtn.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
    this._loadMoreBtn.disabled = false;
    this._loadMoreBtn = null;
  }

  _buildPageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);
    return url.toString();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this._clearPrefetch();
    this._prefetchGuardOff?.();
  }
}

class ConstructorFilteredPaginatedBase extends ConstructorPaginatedBase {
  _surfaceName() {
    throw new Error(`[${this.constructor.name}] must implement _surfaceName()`);
  }

  _sortRadioIdPrefix() {
    throw new Error(`[${this.constructor.name}] must implement _sortRadioIdPrefix()`);
  }

  _applyEventName() {
    return `constructor-${this._surfaceName()}:apply-filters`;
  }

  _dataEventName() {
    return `constructor-${this._surfaceName()}:data`;
  }

  _initFilterAndSort() {
    this._selectedFilters = this._parseUrlFilters();
    this._selectedSort = this._parseUrlSort();
    this._onApplyFiltersBound = this._onApplyFilters.bind(this);
    this.addEventListener(this._applyEventName(), this._onApplyFiltersBound);
    this._initSortDrawer();
  }

  _teardownFilterAndSort() {
    this._sortAbort?.abort();
    if (this._onApplyFiltersBound) {
      this.removeEventListener(this._applyEventName(), this._onApplyFiltersBound);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this._teardownFilterAndSort();
  }

  _afterFetchResult(result) {
    this._renderSortOptions(result?.response?.sort_options || []);
    this.dispatchEvent(
      new CustomEvent(this._dataEventName(), {
        bubbles: true,
        composed: true,
        detail: { response: result?.response ?? null },
      }),
    );
  }

  _onApplyFilters(event) {
    this._selectedFilters = event.detail?.filters || {};
    this._resetForFilterChange();
    this._load();
  }

  _parseUrlSort() {
    const v = new URL(window.location.href).searchParams.get('sort_by');
    if (!v) return null;
    if (v.endsWith('-ascending')) return { sortBy: v, sortOrder: 'ascending' };
    if (v.endsWith('-descending')) return { sortBy: v, sortOrder: 'descending' };
    return { sortBy: v, sortOrder: 'descending' };
  }

  _parseUrlFilters() {
    const out = {};
    const configs = window.constructorFacetsConfig || [];
    if (!configs.length) return out;
    const url = new URL(window.location.href);
    url.searchParams.forEach((urlVal, key) => {
      if (!key.startsWith('filter.')) return;
      configs
        .filter((c) => String(c.shopifyFilterParam || '').trim() === key)
        .forEach((c) => {
          const isToggle =
            String(c.displayType || '')
              .toLowerCase()
              .trim() === 'toggle';
          let cv = null;
          if (isToggle) {
            // Map the Shopify URL value to Constructor's truthy value for this facet.
            // Falls back to 'True' for facets that don't have toggle_constructor_value set yet.
            if (String(c.toggleUrlValue || '').trim() === urlVal) {
              cv = String(c.toggleConstructorValue || 'True');
            }
          } else {
            cv = urlVal;
          }
          if (cv == null) return;
          if (!out[c.name]) out[c.name] = [];
          if (!out[c.name].includes(cv)) out[c.name].push(cv);
        });
    });
    return out;
  }

  /** Constructor API filters — keys are facet names, values string[] */
  _normalizedFilters() {
    const raw = this._selectedFilters;
    if (!raw || typeof raw !== 'object') return {};
    const out = {};
    Object.keys(raw).forEach((k) => {
      const arr = raw[k];
      if (!Array.isArray(arr) || !arr.length) return;
      const strings = [...new Set(arr.map((x) => String(x)))].filter(Boolean);
      if (strings.length) out[k] = strings;
    });
    return out;
  }

  /** Merge filter + sort into an existing Constructor request params object (mutates and returns it). */
  _applyFilterAndSortTo(params) {
    const facets = this._normalizedFilters();
    const hasSizeFilter = Array.isArray(facets['size']) && facets['size'].length > 0;
    if (hasSizeFilter) {
      facets['availableForSale'] = ['True'];
      params.hiddenFacets = ['availableForSale'];
    }
    if (Object.keys(facets).length > 0) params.filters = facets;
    if (this._selectedSort?.sortBy) {
      params.sortBy = this._selectedSort.sortBy;
      params.sortOrder = this._selectedSort.sortOrder;
    }
    return params;
  }

  _resetForFilterChange() {
    // Safe on search too — it simply doesn't track _fetchedProductCount.
    this._fetchedProductCount = 0;
    this.currentPage = 1;
    this._isInitialRender = true;
    this._clearPrefetch?.();
    if (this._emptyState) this._emptyState.hidden = true;

    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('page')) {
        url.searchParams.delete('page');
        history.replaceState({}, '', url.toString());
      }
    } catch (_) {
      /* ignore */
    }
  }

  _initSortDrawer() {
    this._sortHosts = [...this.querySelectorAll('[data-constructor-sort-host]')];
    this._sortLabels = [...this.querySelectorAll('[data-constructor-sort-label]')];
    this._sortCheckIconHtml = document.getElementById('ConstructorSortCheckIcon')?.innerHTML || '';
    this._onSortRadioChange = this._onSortRadioChange.bind(this);
    this._sortAbort = new AbortController();
    const signal = this._sortAbort.signal;

    this._sortHosts.forEach((host) => {
      // Let the browser toggle <details>; just sync .menu-opening + body lock around it.
      host.addEventListener(
        'toggle',
        () => {
          if (host.open) {
            host.classList.add('menu-opening');
            document.body.classList.add(
              window.innerWidth < ConstructorBase.MOBILE_BODY_LOCK_PX ? 'overflow-hidden-mobile' : 'overflow-hidden',
            );
          } else {
            host.classList.remove('menu-opening');
            document.body.classList.remove('overflow-hidden', 'overflow-hidden-mobile');
          }
        },
        { signal },
      );
      host.querySelectorAll('.js-details-close').forEach((el) => {
        el.addEventListener(
          'click',
          (e) => {
            e.preventDefault();
            this._closeSortDrawer(host);
          },
          { signal },
        );
      });
      host.addEventListener(
        'click',
        (e) => {
          if (e.target?.classList?.contains('mobile_sort_container')) {
            this._closeSortDrawer(host);
          }
        },
        { signal },
      );
      host.addEventListener('change', this._onSortRadioChange, { signal });
    });
  }

  _closeSortDrawer(host) {
    host.classList.remove('menu-opening');
    document.body.classList.remove('overflow-hidden', 'overflow-hidden-mobile');
    setTimeout(() => host.removeAttribute('open'), ConstructorBase.DRAWER_CLOSE_MS);
  }

  _onSortRadioChange(event) {
    const radio = event.target;
    if (!radio?.matches('input[type="radio"][name="sort_by"]')) return;
    const host = radio.closest('[data-constructor-sort-host]');
    const sortBy = radio.value;
    const sortOrder = radio.dataset.sortOrder || 'descending';
    this._selectedSort = sortBy ? { sortBy, sortOrder } : null;
    this._pushSortUrl();
    this._renderSortOptions(this._lastSortOptions || []);
    if (host) this._closeSortDrawer(host);
    this._resetForFilterChange();
    this._load();
  }

  _pushSortUrl() {
    const url = new URL(window.location.href);
    const isDefault = this._sortDefaultSortBy && this._selectedSort?.sortBy === this._sortDefaultSortBy;
    if (this._selectedSort?.sortBy && !isDefault) {
      url.searchParams.set('sort_by', this._selectedSort.sortBy);
    } else {
      url.searchParams.delete('sort_by');
    }
    if (window.location.search !== url.search) history.replaceState({}, '', url);
  }

  _renderSortOptions(options) {
    this._lastSortOptions = options || [];
    if (!this._sortHosts?.length) return;
    const visible = this._lastSortOptions.filter((o) => !o.hidden);
    if (!visible.length) return;

    // Capture the natural baseline default exactly once, before any user selection.
    // Locked in for the session: prevents URL hygiene from flipping if Constructor flags
    // a different option as `selected` mid-session (e.g. A/B variant rotation).
    if (!this._sortDefaultSortBy) {
      const naturalDefault = visible.find((o) => o.status === 'selected') || visible[0];
      this._sortDefaultSortBy = naturalDefault?.sort_by || null;
      if (!this._selectedSort && naturalDefault) {
        this._selectedSort = { sortBy: naturalDefault.sort_by, sortOrder: naturalDefault.sort_order };
      }
    }

    const selectedSortBy = this._selectedSort?.sortBy || this._sortDefaultSortBy;
    const selectedOpt = visible.find((o) => o.sort_by === selectedSortBy);
    const selectedLabel = (window.constructorSortConfig?.[selectedOpt?.sort_by] || selectedOpt?.display_name) || '';
    const idPrefix = this._sortRadioIdPrefix();

    const itemsHtml = visible
      .map((o, idx) => {
        const v = String(o.sort_by).replace(/"/g, '&quot;');
        const order = String(o.sort_order || '').replace(/"/g, '&quot;');
        const label = String(window.constructorSortConfig?.[o.sort_by] || o.display_name || o.sort_by).replace(/</g, '&lt;');
        const checked = o.sort_by === selectedSortBy ? ' checked' : '';
        return `
          <li class="list-menu__item facets__item">
            <label class="facet-checkbox" for="${idPrefix}__hostIdx__-${idx}">
              <input
                type="radio"
                name="sort_by"
                value="${v}"
                id="${idPrefix}__hostIdx__-${idx}"
                data-sort-order="${order}"
                class="facet-checkbox-sort visually-hidden"${checked}
              >
              ${this._sortCheckIconHtml}
              <span aria-hidden="true">${label}</span>
            </label>
          </li>
        `;
      })
      .join('');

    this._sortHosts.forEach((host, hostIdx) => {
      const list = host.querySelector('[data-constructor-sort-list]');
      if (list) list.innerHTML = itemsHtml.replace(/__hostIdx__/g, String(hostIdx));
    });

    if (selectedLabel) {
      this._sortLabels.forEach((el) => {
        el.textContent = selectedLabel;
      });
    }
  }
}
